import { addDoc, collection, doc, getFirestore, onSnapshot, serverTimestamp, setDoc } from './instrumentedFirestore';
import { getFirebaseApp } from './app';
import { COLLECTIONS } from '@constants/firestorePaths';

function db() {
    return getFirestore(getFirebaseApp());
}

function stripUndefined(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    const next = {};
    for (const [k, v] of Object.entries(obj)) {
        if (typeof v === 'undefined') continue;
        next[k] = v;
    }
    return next;
}

/**
 * Live list of school (tenant) registrations for the owner console.
 * Sorted client-side to avoid composite index requirements on new projects.
 */
export function subscribeTenants(onNext, onError) {
    return onSnapshot(
        collection(db(), COLLECTIONS.TENANTS),
        (snap) => {
            const rows = snap.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .sort((a, b) => {
                    const ta = a.createdAt?.toMillis?.() ?? a.createdAt ?? 0;
                    const tb = b.createdAt?.toMillis?.() ?? b.createdAt ?? 0;
                    return tb - ta;
                });
            onNext(rows);
        },
        onError
    );
}

export function generateTenantId() {
    return doc(collection(db(), COLLECTIONS.TENANTS)).id;
}

/**
 * Register a new school tenant record (Firestore). Creating the Auth user for the
 * school admin is done via `pnpm run create-tenant-admin` or a future Cloud Function.
 */
export async function createTenantRegistration(payload, createdByUid) {
    const clean = stripUndefined(payload);
    const docRef = await addDoc(collection(db(), COLLECTIONS.TENANTS), {
        ...clean,
        createdByUid,
        createdAt: serverTimestamp(),
        status: clean.status || 'pending',
    });
    return docRef.id;
}

export async function createTenantRegistrationWithId({ tenantId, payload, createdByUid, adminUid, adminEmail }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const clean = stripUndefined(payload);
    await setDoc(
        doc(db(), COLLECTIONS.TENANTS, tenantId),
        stripUndefined({
            ...clean,
            createdByUid,
            createdAt: serverTimestamp(),
            status: clean.status || 'pending',
            adminUid,
            adminEmail,
        }),
        { merge: true }
    );
    return tenantId;
}

export async function updateTenantProfile(tenantId, patch) {
    if (!tenantId) throw new Error('Missing tenantId');
    const clean = stripUndefined(patch);
    await setDoc(doc(db(), COLLECTIONS.TENANTS, tenantId), clean, { merge: true });
    return { tenantId, ...clean };
}

/** Live tenant profile (e.g. `attendanceConfig`) without relying on cached auth state. */
export function subscribeTenantDocument({ tenantId, onNext, onError }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const ref = doc(db(), COLLECTIONS.TENANTS, tenantId);
    return onSnapshot(
        ref,
        (snap) => {
            onNext?.(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        },
        (err) => onError?.(err)
    );
}
